import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * The product's raised plane.
 *
 * `surface` carries the whole treatment — fill, sheen, hairline, lit top edge,
 * shadow — so every panel in the app is layered the same way and a page never
 * has to hand-roll one. It is a utility rather than a set of classes so
 * tailwind-merge still lets a caller override the fill (`bg-muted/30`) without
 * losing the bevel.
 *
 * SPACING is one variable, `--card-spacing`, shared by the header, content and
 * footer. Every part reading the same number is what lets a caller retune a
 * whole card at once — `className="[--card-spacing:--spacing(8)]"` — instead of
 * chasing four different paddings that then disagree. `size="sm"` is the same
 * mechanism with a smaller default.
 *
 * It also unlocks edge-to-edge content: `-mx-(--card-spacing)` on a child
 * cancels the inset exactly, however the card was tuned, so a table or a list
 * can bleed to the border and still line up with the text above it.
 */
function Card({
  className,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        "surface group/card flex flex-col gap-(--card-spacing) rounded-xl bg-card py-(--card-spacing) text-card-foreground shadow-[var(--elev-1)]",
        "[--card-spacing:--spacing(6)] data-[size=sm]:[--card-spacing:--spacing(4)]",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "text-[0.9375rem] leading-tight font-semibold tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

/**
 * Top-trailing slot — a button, a badge, a price.
 *
 * `justify-self-end` rather than `right-0`: in Arabic the card flips and the
 * action has to follow the text direction with it.
 */
function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-(--card-spacing)", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center px-(--card-spacing) [.border-t]:pt-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
